function CompaniesView({
  companySearchText,
  setCompanySearchText,
  companySort,
  setCompanySort,
  firmaListesi,
  filteredFirmaListesi,
  setSelectedCompany,
  themeMode = "light"
}) {
  const isDark = themeMode === "dark";
  const colors = {
    text: isDark ? "#e5edf6" : "#111827",
    muted: isDark ? "#94a3b8" : "#64748b",
    surface: isDark ? "#111827" : "#ffffff",
    softSurface: isDark ? "#1f2937" : "#f9fafb",
    border: isDark ? "#334155" : "#e5e7eb",
    inputBg: isDark ? "#111827" : "#ffffff",
    shadow: isDark ? "0 10px 28px rgba(0,0,0,0.30)" : "0 6px 18px rgba(0,0,0,0.05)"
  };

  const summaryCardStyle = (accentColor, lightBackground) => ({
    background: isDark ? colors.surface : lightBackground,
    padding: 20,
    borderRadius: 18,
    border: isDark ? `1px solid ${colors.border}` : "none",
    borderLeft: `5px solid ${accentColor}`,
    boxShadow: colors.shadow
  });

  const getCompanyRiskRank = (firma) => {
    if (Number(firma.kritikProjeSayisi || 0) > 0) return 1;
    if (Number(firma.riskliProjeSayisi || 0) > 0) return 2;
    return 3;
  };

  const getCompanyHealth = (firma) => {
    const projeler = firma.projeler || [];

    const kritikProjeSayisi = Number(firma.kritikProjeSayisi || 0);
    const riskliProjeSayisi = Number(firma.riskliProjeSayisi || 0);
    const sonlandirilanProjeSayisi = Number(firma.sonlandirilanProjeSayisi || 0);

    const bitirmeBelgesiEksikSayisi = projeler.filter(
      (project) =>
        !project.projeBitirmeBelgesi ||
        project.projeBitirmeBelgesi === "Yok" ||
        project.projeBitirmeBelgesi === "Kontrol Edilecek"
    ).length;

    const yazilimTescilEksikSayisi = projeler.filter((project) => {
      const kategori = String(project.projeKategorisi || "")
        .trim()
        .toLocaleLowerCase("tr-TR")
        .replaceAll("ı", "i");

      return kategori.includes("yazilim") && project.yazilimTescilBelgesi !== "Var";
    }).length;

    const degerlendirmeBekleyenSayisi = projeler.filter(
      (project) => String(project.projeStbKodu || "").trim().toUpperCase() === "DB"
    ).length;

    let score = 100;

    score -= kritikProjeSayisi * 25;
    score -= riskliProjeSayisi * 10;
    score -= bitirmeBelgesiEksikSayisi * 5;
    score -= yazilimTescilEksikSayisi * 5;
    score -= sonlandirilanProjeSayisi * 2;
    score -= degerlendirmeBekleyenSayisi * 3;

    const finalScore = Math.max(score, 0);

    if (finalScore < 60) {
      return {
        score: finalScore,
        label: "Kritik",
        icon: "🔴",
        background: "#fee2e2",
        color: "#991b1b"
      };
    }

    if (finalScore < 80) {
      return {
        score: finalScore,
        label: "Takip Edilmeli",
        icon: "🟠",
        background: "#fef3c7",
        color: "#92400e"
      };
    }

    return {
      score: finalScore,
      label: "Sağlıklı",
      icon: "🟢",
      background: "#dcfce7",
      color: "#166534"
    };
  };

  const sortedCompanies = [...filteredFirmaListesi].sort((a, b) => {
    const riskDiff = getCompanyRiskRank(a) - getCompanyRiskRank(b);

    if (riskDiff !== 0) return riskDiff;

    return String(a.firmaKisaAdi || a.firmaAdi || "").localeCompare(
      String(b.firmaKisaAdi || b.firmaAdi || ""),
      "tr"
    );
  });

  const healthSummary = firmaListesi.reduce(
  (summary, firma) => {
    const health = getCompanyHealth(firma);

    if (health.label === "Kritik") {
      summary.kritik += 1;
    } else if (health.label === "Takip Edilmeli") {
      summary.takip += 1;
    } else {
      summary.saglikli += 1;
    }

    return summary;
  },
  {
    kritik: 0,
    takip: 0,
    saglikli: 0
  }
  );

  return (
  <div>
    <h2 style={{ color: colors.text }}>Firmalar</h2>

      <input
        type="text"
        placeholder="Firma kısa adı, firma adı, proje adı veya STB kodu ara..."
        value={companySearchText}
        onChange={(e) => setCompanySearchText(e.target.value)}
        style={{
          width: "100%",
          padding: "14px 18px",
          marginTop: 18,
          marginBottom: 24,
          borderRadius: 14,
          border: `1px solid ${isDark ? "#334155" : "#dbe2ea"}`,
          background: colors.inputBg,
          color: colors.text,
          fontSize: 14,
          outline: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)"
        }}
      />

      <div style={{ marginBottom: 24 }}>
      <select
        value={companySort}
        onChange={(e) => setCompanySort(e.target.value)}
        style={{
          padding: "12px 16px",
          borderRadius: 12,
          border: `1px solid ${isDark ? "#334155" : "#dbe2ea"}`,
          background: colors.inputBg,
          color: colors.text,
          fontSize: 14
        }}
      >
        <option value="firmaAdi">Firma Adına Göre</option>
        <option value="projeSayisi">Toplam Proje Sayısına Göre</option>
        <option value="aktif">Aktif Proje Sayısına Göre</option>
        <option value="riskli">Riskli Proje Sayısına Göre</option>
        <option value="kritik">Kritik Proje Sayısına Göre</option>
      </select>
    </div>

      <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 18,
            marginBottom: 28
          }}
        >
          <div
            style={summaryCardStyle("#38bdf8", "#eef4ff")}
          >
            <div
              style={{
                fontSize: 13,
                color: isDark ? "#93c5fd" : "#6b7280",
                marginBottom: 8
              }}
            >
              Toplam Firma
            </div>

            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                color: isDark ? "#e0f2fe" : "#124254"
              }}
            >
              {firmaListesi.length}
            </div>
          </div>

          <div
            style={summaryCardStyle("#22c55e", "#dcfce7")}
          >
            <div
              style={{
                fontSize: 13,
                color: "#166534",
                marginBottom: 8
              }}
            >
              Toplam Aktif Proje
            </div>

            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                color: "#166534"
              }}
            >
              {firmaListesi.reduce(
                (total, firma) => total + firma.aktifProjeSayisi,
                0
              )}
            </div>
          </div>

          <div
            style={summaryCardStyle("#f59e0b", "#fff7d6")}
          >
            <div
              style={{
                fontSize: 13,
                color: "#92400e",
                marginBottom: 8
              }}
            >
              Riskli Proje
            </div>

            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                color: "#92400e"
              }}
            >
              {firmaListesi.reduce(
                (total, firma) => total + firma.riskliProjeSayisi,
                0
              )}
            </div>
          </div>

          <div
            style={summaryCardStyle("#ef4444", "#fee2e2")}
          >
            <div
              style={{
                fontSize: 13,
                color: "#991b1b",
                marginBottom: 8
              }}
            >
              Kritik Proje
            </div>

            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                color: "#991b1b"
              }}
            >
              {firmaListesi.reduce(
                (total, firma) => total + firma.kritikProjeSayisi,
                0
              )}
            </div>
          </div>

          <div
            style={summaryCardStyle("#ef4444", "#fee2e2")}
          >
            <div style={{ fontSize: 13, color: "#991b1b", marginBottom: 8 }}>
              Kritik Firma
            </div>

            <div style={{ fontSize: 32, fontWeight: 800, color: "#991b1b" }}>
              {healthSummary.kritik}
            </div>
          </div>

          <div
            style={summaryCardStyle("#f59e0b", "#fef3c7")}
          >
            <div style={{ fontSize: 13, color: "#92400e", marginBottom: 8 }}>
              Takip Edilmeli Firma
            </div>

            <div style={{ fontSize: 32, fontWeight: 800, color: "#92400e" }}>
              {healthSummary.takip}
            </div>
          </div>

          <div
            style={summaryCardStyle("#22c55e", "#dcfce7")}
          >
            <div style={{ fontSize: 13, color: "#166534", marginBottom: 8 }}>
              Sağlıklı Firma
            </div>

            <div style={{ fontSize: 32, fontWeight: 800, color: "#166534" }}>
              {healthSummary.saglikli}
            </div>
          </div>

        </div>

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 18
      }}
    >
      {sortedCompanies.map((firma) => {
        const health = getCompanyHealth(firma);
        const firmaGorunenAdi = firma.firmaKisaAdi || firma.companyShortName || firma.kisaAd || firma.firmaAdi;
        const isCritical = firma.kritikProjeSayisi > 0;
        const isRisky = !isCritical && firma.riskliProjeSayisi > 0;
        const accentColor = isCritical ? "#ef4444" : isRisky ? "#f59e0b" : "#22c55e";

        return (
        <div
          key={firma.firmaAdi}
          onClick={() => setSelectedCompany(firma)}
          style={{
            background:
              isDark
                ? colors.surface
                : isCritical
                ? "#fff5f5"
                : isRisky
                ? "#fffbea"
                : "#ffffff",

            padding: 22,
            borderRadius: 18,

            border:
              isDark
                ? `1px solid ${colors.border}`
                : isCritical
                ? "1px solid #fca5a5"
                : isRisky
                ? "1px solid #facc15"
                : "1px solid #e5e7eb",
            borderTop: `4px solid ${accentColor}`,

            cursor: "pointer",
            boxShadow: colors.shadow,
            transition: "0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <h3
          style={{
            marginTop: 0,
            marginBottom: 10,
            fontSize: 15,
            lineHeight: 1.4,
            fontWeight: 700,
            color: colors.text,
            minHeight: 64
          }}
        >
          {firmaGorunenAdi}
        </h3>

        {firmaGorunenAdi !== firma.firmaAdi && (
          <div
            style={{
              marginTop: -4,
              marginBottom: 12,
              color: colors.muted,
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.4
            }}
          >
            {firma.firmaAdi}
          </div>
        )}

        <div
          style={{
            marginBottom: 14,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            background:
              isCritical
                ? "#fee2e2"
                : isRisky
                ? "#fef3c7"
                : "#dcfce7",

            color:
              isCritical
                ? "#991b1b"
                : isRisky
                ? "#92400e"
                : "#166534"
          }}
        >
          {isCritical
            ? "🔴 Kritik"
            : isRisky
            ? "🟡 Riskli"
            : "🟢 Stabil"}
        </div>

        <div
          style={{
            marginBottom: 14,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            background: health.background,
            color: health.color
          }}
        >
          {health.icon} Sağlık Skoru: {health.score} / 100 · {health.label}
        </div>

          <div style={{ display: "grid", gap: 10, color: colors.text }}>
            <div style={{ color: isDark ? "#dbeafe" : colors.text }}>
              <strong>Toplam Proje:</strong> {firma.projeSayisi}
            </div>

            <div style={{ color: isDark ? "#86efac" : "#166534" }}>
              <strong>Aktif:</strong> {firma.aktifProjeSayisi}
            </div>

            <div style={{ color: isDark ? "#fbbf24" : "#92400e" }}>
              <strong>Riskli:</strong> {firma.riskliProjeSayisi}
            </div>

            <div style={{ color: isDark ? "#fca5a5" : "#991b1b" }}>
              <strong>Kritik:</strong> {firma.kritikProjeSayisi}
            </div>

            <div style={{ color: isDark ? "#fda4af" : "#991b1b" }}>
              <strong>Sonlandırılan:</strong> {firma.sonlandirilanProjeSayisi}
            </div>

            <div style={{ color: isDark ? "#fdba74" : "#92400e" }}>
            <strong>Tamamlanan:</strong> {firma.tamamlananProjeSayisi}
          </div>

          <div style={{ color: isDark ? "#93c5fd" : "#2563eb" }}>
            <strong>Bitirme Belgesi:</strong> {firma.bitirmeBelgesiSayisi}
          </div>

          </div>
        </div>
        );
        })}
    </div>
  </div>
);
}
export default CompaniesView;  
